import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

import CLocalizedTextStringStatic from '../../../components/CLocalizedTextStringStatic';
import CPageModule from '../../../components/CPageModule';
import CPageModulePlot from '../../../components/CPageModulePlot';
import CResponsiveGrid from '../../../components/CResponsiveGrid';
import { type IFundPortrait, type IShareClassPortrait } from '../../../components/IFundPortrait';
import { ETableIds } from '../../../enums/ETableIds';
import { updateStateAttribute } from '../../../helpers/helper';
import { useAppSelector } from '../../../hooks/hooks';
import { type IResponse } from '../../../models/IResponse';
import { type PlotlyResponse } from '../../../models/PlotlyModel';
import { type TPandasTable } from '../../../models/TPandasTable';
import { SvMultifundFactsheetService } from '../../../services/SvMultifundFactsheetService';
import CTable from '../../playground/CTable';
import CFactsheetOverviewFundPortrait from './_components/overview/CFactsheetOverviewFundPortrait';
import CFactsheetOverviewKeyInfo from './_components/overview/CFactsheetOverviewKeyInfo';

interface State {
  fundPortrait: IResponse<IFundPortrait>;
  shareClassPortraits: IResponse<IShareClassPortrait[]>;
  overviewTable: IResponse<TPandasTable> & { overlayMessage?: string };
  overviewChart: IResponse<PlotlyResponse> & { overlayMessage?: string };
}

/**
 * This is the overview page for the factsheet.
 * @constructor
 */
export function PMultifundFactsheetIndex() {
  const { locale, funds } = useAppSelector((state) => state.dashboard);
  const { simpleFundKey } = useParams();
  const [state, setState] = useState<State>({
    fundPortrait: {
      state: {
        isLoading: false,
        isError: false
      }
    },
    shareClassPortraits: {
      state: {
        isLoading: false,
        isError: false
      }
    },
    overviewTable: {
      state: {
        isLoading: true,
        isError: false
      }
    },
    overviewChart: {
      state: {
        isLoading: true,
        isError: false
      }
    }
  });

  const fetchFundPortrait = async () => {
    if (!simpleFundKey) return;

    updateStateAttribute<State>(
      {
        fundPortrait: {
          ...state.fundPortrait,
          state: {
            ...state.fundPortrait.state,
            isLoading: true
          }
        }
      },
      setState
    );

    const res = await SvMultifundFactsheetService.getFundPortrait(simpleFundKey);
    const isError = !(await res.isSuccess());

    updateStateAttribute<State>(
      {
        fundPortrait: {
          state: {
            isLoading: false,
            isError,
            strError: isError ? await res.getMessage() : undefined
          },
          data: await res.getData()
        }
      },
      setState
    );
  };

  const fetchShareClassPortraits = async () => {
    if (!simpleFundKey) return;

    updateStateAttribute<State>(
      {
        shareClassPortraits: {
          ...state.shareClassPortraits,
          state: {
            ...state.shareClassPortraits.state,
            isLoading: true
          }
        }
      },
      setState
    );

    const res = await SvMultifundFactsheetService.getShareClassPortraits(simpleFundKey);
    const isError = !(await res.isSuccess());

    updateStateAttribute<State>(
      {
        shareClassPortraits: {
          state: {
            isLoading: false,
            isError,
            strError: isError ? await res.getMessage() : undefined
          },
          data: await res.getData()
        }
      },
      setState
    );
  };

  const fetchOverviewTable = async () => {
    updateStateAttribute<State>(
      {
        overviewTable: {
          ...state.overviewTable,
          state: {
            ...state.overviewTable.state,
            isLoading: true
          }
        }
      },
      setState
    );
    const res = await SvMultifundFactsheetService.getOverviewTable(simpleFundKey ?? '');
    const isSuccess = await res.isSuccess();
    const data = await res.getData();
    // Masked results are flagged as unsuccessful, but still carry data
    const isError = !isSuccess && !data;
    updateStateAttribute<State>(
      {
        overviewTable: {
          state: {
            isLoading: false,
            strError: isError ? await res.getMessage() : undefined,
            isError
          },
          data,
          overlayMessage: !isSuccess && data ? await res.getMessage() : undefined
        }
      },
      setState
    );
  };

  const fetchOverviewChart = async () => {
    updateStateAttribute<State>(
      {
        overviewChart: {
          ...state.overviewChart,
          state: {
            ...state.overviewChart.state,
            isLoading: true
          }
        }
      },
      setState
    );
    const res = await SvMultifundFactsheetService.getOverviewChart(simpleFundKey ?? '');
    const isSuccess = await res.isSuccess();
    const data = await res.getData();
    // Masked results are flagged as unsuccessful, but still carry data
    const isError = !isSuccess && !data;
    updateStateAttribute<State>(
      {
        overviewChart: {
          state: {
            isLoading: false,
            strError: isError ? await res.getMessage() : undefined,
            isError
          },
          data,
          overlayMessage: !isSuccess && data ? await res.getMessage() : undefined
        }
      },
      setState
    );
  };

  useEffect(() => {
    (async () => {
      await Promise.all([fetchFundPortrait(), fetchShareClassPortraits(), fetchOverviewTable(), fetchOverviewChart()]);
    })();
  }, [locale, simpleFundKey]);

  const textLabel = CLocalizedTextStringStatic(`multifundFactsheet.${simpleFundKey}.overview.description`);

  return (
    <CPageModule fullWidth>
      <CPageModule submodule>
        <h2>{funds.find((e) => e.id === simpleFundKey)?.fullName}</h2>
        <p>{textLabel}</p>
      </CPageModule>
      <CPageModulePlot
        data={state.overviewChart.data?.data}
        layout={state.overviewChart.data?.layout}
        overlayMessage={
          state.overviewChart.overlayMessage
            ? {
                mdText: state.overviewChart.overlayMessage,
                wordLength: state.overviewChart.overlayMessage.split(' ').length
              }
            : undefined
        }
        apiRequestState={state.overviewChart.state}
        showWhen={!!state.overviewChart.data}
        aspectRatio={4}
        fitToViewport
      />
      {!!state.overviewChart.data && (
        <CPageModule submodule apiRequestState={state.overviewTable.state} showContent={!!state.overviewTable.data}>
          <CResponsiveGrid maxColumns={2} style={{ columnGap: '1.5rem', rowGap: '1.5rem' }}>
            {state.fundPortrait.data && (
              <div className={'flex items-center'}>
                <CFactsheetOverviewKeyInfo
                  economicTargetAllocation={state.fundPortrait.data.economicTargetAllocation}
                  maximumLeverage={state.fundPortrait.data.maximumLeverage}
                  indexOptionPosition={state.fundPortrait.data.indexOptionPosition}
                />
              </div>
            )}
            <CTable
              id={ETableIds.MFFS_PERFORMANCE_TABLE}
              data={state.overviewTable.data}
              overlayMessage={
                state.overviewTable.overlayMessage
                  ? {
                      mdText: state.overviewTable.overlayMessage,
                      wordLength: state.overviewTable.overlayMessage.split('<br/>')[0].split(' ').length
                    }
                  : undefined
              }
              rowConfig={{}}
              columnConfig={[
                {},
                {
                  header: {
                    style: () => {
                      return { textAlign: 'right' };
                    }
                  },
                  body: {
                    style: () => {
                      return { textAlign: 'right' };
                    }
                  }
                }
              ]}
            />
          </CResponsiveGrid>
        </CPageModule>
      )}
      {state.fundPortrait.data && state.shareClassPortraits.data && (
        <CFactsheetOverviewFundPortrait
          className={funds.find((e) => e.id === simpleFundKey)?.fullName ?? ''}
          fundPortrait={state.fundPortrait.data}
          shareClassPortraits={state.shareClassPortraits.data ?? []}
        />
      )}
      <pre>{JSON.stringify(state.overviewTable?.overlayMessage?.split('<br/>')[0].split(' ').length)}</pre>
    </CPageModule>
  );
}
